import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  GripVertical,
  Info,
  Loader2,
  Play,
  Save,
  Search,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { cn, showToast } from '../utils/helpers';
import {
  DEMO_MAX_ITEMS,
  DemoContentItem,
  DemoContentMode,
  DemoContentPerson,
  DemoBrowseItem,
  DemoOrderBy,
  DemoPersonContent,
  PersonSuggestion,
  SITE_URL,
  getDemoConfig,
  getDemoPersonContent,
  saveDemoConfig,
  searchDemoPeople,
} from '../services/demoContent';

// ============================================================================
// Website App Demo — curate what content plays inside the phone demo on the
// xuman.ai homepage, for ALL visitors. Default = the site's live explore
// feed; Custom = the numbered line-up built here: every selected item gets a
// position (1..50) that is the exact global play order. Press Update to
// publish — visitors then see exactly that content in exactly that order.
// ============================================================================

/** Keep items sorted by position and densely renumbered 1..N. */
const renumber = (items: DemoContentItem[]): DemoContentItem[] =>
  items
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((it, index) => ({ ...it, position: index + 1 }));

const DemoContent: React.FC = () => {
  // ---- Persisted config state ----
  const [mode, setMode] = useState<DemoContentMode>('default');
  const [orderBy, setOrderBy] = useState<DemoOrderBy>('manual');
  const [people, setPeople] = useState<DemoContentPerson[]>([]);
  const [items, setItems] = useState<DemoContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [storeStatus, setStoreStatus] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string>('');

  // ---- Browse state ----
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<PersonSuggestion[]>([]);
  const [activePerson, setActivePerson] = useState<DemoPersonContent | null>(
    null
  );
  const [loadingPersonId, setLoadingPersonId] = useState<number | null>(null);
  const personCacheRef = useRef<Map<number, DemoPersonContent>>(new Map());
  // Bumped whenever the cache gains a person so line-up thumbnails refresh.
  const [, setCacheVersion] = useState(0);

  // ---- Drag-and-drop reorder state ----
  // Rows are only draggable while the grip handle is pressed — otherwise the
  // position <input> would start drags instead of taking focus/selection.
  const dragIdRef = useRef<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [dragEnabledId, setDragEnabledId] = useState<string | null>(null);

  const loadPersonIntoCache = useCallback(async (userId: number) => {
    if (personCacheRef.current.has(userId)) {
      return personCacheRef.current.get(userId) || null;
    }
    try {
      const content = await getDemoPersonContent(userId);
      personCacheRef.current.set(userId, content);
      setCacheVersion((v) => v + 1);
      return content;
    } catch {
      return null;
    }
  }, []);

  // Load the current global config from the backend, then prefetch each
  // featured person's content so line-up rows get thumbnails/captions.
  useEffect(() => {
    (async () => {
      try {
        const config = await getDemoConfig();
        setMode(config.mode);
        setOrderBy(config.orderBy);
        setPeople(config.people);
        setItems(renumber(config.items));
        setLastUpdatedAt(config.updatedAt || '');
        setStoreStatus(null);
        await Promise.all(
          config.people.map((p) => loadPersonIntoCache(p.userId))
        );
      } catch {
        showToast('error', 'Could not load the current demo configuration');
        setStoreStatus('error');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadPersonIntoCache]);

  // Debounced people search
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        setResults(await searchDemoPeople(q));
      } catch {
        showToast('error', 'Search is unavailable right now');
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const openPerson = useCallback(
    async (userId: number) => {
      const cached = personCacheRef.current.get(userId);
      if (cached) {
        setActivePerson(cached);
        return;
      }
      setLoadingPersonId(userId);
      const content = await loadPersonIntoCache(userId);
      setLoadingPersonId(null);
      if (content) {
        setActivePerson(content);
      } else {
        showToast('error', "Could not load this person's content");
      }
    },
    [loadPersonIntoCache]
  );

  // ---- Selection helpers ----

  /** Owner's current xumanRating for the auto-order preview (-1 = unknown). */
  const ratingFor = (userId: number): number =>
    personCacheRef.current.get(userId)?.person.xumanRating ?? -1;

  /**
   * What the site will play, in order. Manual → admin positions. Xuman
   * rating → previewed here from cached ratings; the backend recomputes from
   * LIVE ratings on every site load (stable sort, manual positions as ties).
   */
  const displayOrder = (source: DemoContentItem[]): DemoContentItem[] => {
    const manual = renumber(source);
    if (orderBy === 'manual') return manual;
    return manual
      .slice()
      .sort((a, b) => ratingFor(b.userId) - ratingFor(a.userId));
  };

  const positionOf = (contentId: string): number | undefined => {
    const index = displayOrder(items).findIndex(
      (it) => it.contentId === contentId
    );
    return index === -1 ? undefined : index + 1;
  };

  const ensurePersonSnapshot = useCallback(
    (person: DemoPersonContent['person']) => {
      setPeople((prev) =>
        prev.some((p) => p.userId === person.userId)
          ? prev
          : [
              ...prev,
              {
                userId: person.userId,
                name: person.name,
                avatarUrl: person.avatarUrl,
              },
            ]
      );
    },
    []
  );

  /** Drop person snapshots that no longer own any selected item. */
  const prunePeople = useCallback((nextItems: DemoContentItem[]) => {
    const featured = new Set(nextItems.map((it) => it.userId));
    setPeople((prev) => prev.filter((p) => featured.has(p.userId)));
  }, []);

  const toggleItem = useCallback(
    (person: DemoPersonContent, contentId: string) => {
      setItems((prev) => {
        const existing = prev.find((it) => it.contentId === contentId);
        if (existing) {
          const next = renumber(
            prev.filter((it) => it.contentId !== contentId)
          );
          prunePeople(next);
          setDirty(true);
          return next;
        }
        if (prev.length >= DEMO_MAX_ITEMS) {
          showToast('error', `Maximum ${DEMO_MAX_ITEMS} items — remove one first`);
          return prev;
        }
        ensurePersonSnapshot(person.person);
        setDirty(true);
        return [
          ...prev,
          {
            contentId,
            userId: person.person.userId,
            position: prev.length + 1,
          },
        ];
      });
    },
    [ensurePersonSnapshot, prunePeople]
  );

  const selectAll = useCallback(
    (person: DemoPersonContent) => {
      setItems((prev) => {
        const selected = new Set(prev.map((it) => it.contentId));
        const toAdd = person.items.filter((it) => !selected.has(it.id));
        if (toAdd.length === 0) return prev;
        const room = DEMO_MAX_ITEMS - prev.length;
        if (room <= 0) {
          showToast('error', `Maximum ${DEMO_MAX_ITEMS} items — remove some first`);
          return prev;
        }
        if (toAdd.length > room) {
          showToast('error', `Only ${room} slot${room === 1 ? '' : 's'} left — added the first ${room}`);
        }
        ensurePersonSnapshot(person.person);
        setDirty(true);
        return [
          ...prev,
          ...toAdd.slice(0, room).map((it, i) => ({
            contentId: it.id,
            userId: person.person.userId,
            position: prev.length + i + 1,
          })),
        ];
      });
    },
    [ensurePersonSnapshot]
  );

  const removeItem = useCallback(
    (contentId: string) => {
      setItems((prev) => {
        const next = renumber(prev.filter((it) => it.contentId !== contentId));
        prunePeople(next);
        return next;
      });
      setDirty(true);
    },
    [prunePeople]
  );

  const clearPerson = useCallback(
    (userId: number) => {
      setItems((prev) => {
        const next = renumber(prev.filter((it) => it.userId !== userId));
        prunePeople(next);
        return next;
      });
      setDirty(true);
    },
    [prunePeople]
  );

  /** Move an item to a 1-based slot; everything else shifts. */
  const moveItem = useCallback((contentId: string, targetPosition: number) => {
    setItems((prev) => {
      const sorted = renumber(prev);
      const fromIndex = sorted.findIndex((it) => it.contentId === contentId);
      if (fromIndex === -1) return prev;
      const clamped = Math.max(1, Math.min(sorted.length, Math.round(targetPosition)));
      if (clamped === fromIndex + 1) return prev;
      const [moved] = sorted.splice(fromIndex, 1);
      sorted.splice(clamped - 1, 0, moved);
      setDirty(true);
      return renumber(sorted.map((it, i) => ({ ...it, position: i + 1 })));
    });
  }, []);

  /** Nudge an item one slot up or down. */
  const moveItemBy = useCallback(
    (contentId: string, delta: number) => {
      const current = items.find((it) => it.contentId === contentId);
      if (current) moveItem(contentId, current.position + delta);
    },
    [items, moveItem]
  );

  const handleDrop = useCallback(
    (targetContentId: string) => {
      const draggedId = dragIdRef.current;
      dragIdRef.current = null;
      setDragOverId(null);
      if (!draggedId || draggedId === targetContentId) return;
      const target = items.find((it) => it.contentId === targetContentId);
      if (target) moveItem(draggedId, target.position);
    },
    [items, moveItem]
  );

  const changeMode = (next: DemoContentMode) => {
    if (next !== mode) {
      setMode(next);
      setDirty(true);
    }
  };

  const changeOrderBy = (next: DemoOrderBy) => {
    if (next !== orderBy) {
      setOrderBy(next);
      setDirty(true);
    }
  };

  const handleUpdate = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const saved = await saveDemoConfig({ mode, orderBy, people, items });
      setMode(saved.mode);
      setOrderBy(saved.orderBy);
      setPeople(saved.people);
      setItems(renumber(saved.items));
      setLastUpdatedAt(saved.updatedAt || '');
      setDirty(false);
      setStoreStatus(null);
      showToast('success', 'Updated — live on the website for all visitors');
    } catch (err: any) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      const message = Array.isArray(data?.message)
        ? data.message.join(', ')
        : data?.message || data?.error;
      if (status === 401) {
        showToast('error', 'Session expired — please log out and back in');
      } else {
        showToast('error', message || 'Failed to update demo content');
      }
    } finally {
      setSaving(false);
    }
  };

  const sortedItems = displayOrder(items);
  const isManualOrder = orderBy === 'manual';
  const customIncomplete = mode === 'custom' && items.length === 0;

  const browseItemFor = (item: DemoContentItem): DemoBrowseItem | null =>
    personCacheRef.current
      .get(item.userId)
      ?.items.find((it) => it.id === item.contentId) || null;

  const personNameFor = (userId: number): string =>
    people.find((p) => p.userId === userId)?.name ||
    personCacheRef.current.get(userId)?.person.name ||
    `User ${userId}`;

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto" />
          <p className="text-muted-foreground mt-4">
            Loading demo configuration...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto scrollbar-thin">
      <div className="max-w-6xl mx-auto p-6 pb-28 space-y-6">
        {/* Store problem banner */}
        {storeStatus && (
          <div className="flex items-start gap-3 rounded-xl border border-yellow-500/30 bg-yellow-500/10 px-4 py-3">
            <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-200/90">
              <p className="font-medium text-yellow-300">
                Could not load the demo config from the backend.
              </p>
              <p className="mt-1">
                Likely causes: the storage backend with GET
                /content/site-demo isn't deployed yet, the APIM operation
                isn't registered, or the SiteConfig migration hasn't run.
                Updating will fail until this loads.
              </p>
            </div>
          </div>
        )}

        {/* Header + mode toggle */}
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Homepage app demo feed
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Controls what plays inside the phone on {SITE_URL} — for every
                visitor.
              </p>
            </div>
            <a
              href={SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-primary/30 bg-primary/10 hover:bg-primary/20 text-primary text-sm font-medium transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              Open website
            </a>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              onClick={() => changeMode('default')}
              className={cn(
                'text-left rounded-xl border p-4 transition-all',
                mode === 'default'
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-muted-foreground/40'
              )}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm text-foreground">
                  Default
                </span>
                {mode === 'default' && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </div>
              <p className="text-xs mt-1 leading-relaxed text-muted-foreground">
                Existing behavior — the live explore feed, shuffled on every
                visit.
              </p>
            </button>
            <button
              onClick={() => changeMode('custom')}
              className={cn(
                'text-left rounded-xl border p-4 transition-all',
                mode === 'custom'
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-muted-foreground/40'
              )}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm text-foreground">
                  Custom
                </span>
                {mode === 'custom' && <Check className="h-4 w-4 text-primary" />}
              </div>
              <p className="text-xs mt-1 leading-relaxed text-muted-foreground">
                Hand-picked content in a numbered order (1–{DEMO_MAX_ITEMS}) —
                the site plays exactly this line-up.
              </p>
            </button>
          </div>

          {customIncomplete && (
            <div className="mt-4 flex items-start gap-2 text-xs leading-relaxed rounded-lg px-3 py-2.5 bg-muted/40 text-muted-foreground">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>
                Custom is on but nothing is selected yet — until items are
                picked (and published with Update), the website keeps showing
                the default feed.
              </span>
            </div>
          )}
        </div>

        {/* Custom curation */}
        {mode === 'custom' && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: search + person browser */}
            <div className="lg:col-span-3 space-y-6">
              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="text-sm font-semibold text-foreground mb-3">
                  Search people
                </h3>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by name (min 2 characters)…"
                    className="w-full rounded-lg bg-background border border-border pl-11 pr-10 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
                  />
                  {searching && (
                    <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-primary" />
                  )}
                </div>

                {results.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {results.map((r) => {
                      const count = items.filter(
                        (it) => it.userId === r.id
                      ).length;
                      return (
                        <button
                          key={r.id}
                          onClick={() => openPerson(r.id)}
                          className={cn(
                            'w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors border',
                            activePerson?.person.userId === r.id
                              ? 'border-primary/50 bg-primary/10'
                              : 'border-transparent hover:bg-primary/5'
                          )}
                        >
                          <Avatar name={r.name} url={r.avatarUrl} size={36} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {r.name}
                            </p>
                            {r.subtitle && (
                              <p className="text-xs text-muted-foreground truncate">
                                {r.subtitle}
                              </p>
                            )}
                          </div>
                          {typeof r.rating === 'number' && r.rating > 0 && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Star className="h-3 w-3 text-yellow-400" />
                              {r.rating.toFixed(1)}
                            </span>
                          )}
                          {count > 0 && (
                            <span className="text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-2 py-0.5 flex-shrink-0">
                              {count} selected
                            </span>
                          )}
                          {loadingPersonId === r.id && (
                            <Loader2 className="h-4 w-4 animate-spin text-primary flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                {!searching &&
                  query.trim().length >= 2 &&
                  results.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-3">
                      No people with content match “{query.trim()}”.
                    </p>
                  )}
              </div>

              {/* Person content grid */}
              {activePerson && (
                <div className="rounded-xl border border-border bg-card p-6">
                  <div className="flex items-center justify-between mb-4 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar
                        name={activePerson.person.name}
                        url={activePerson.person.avatarUrl || undefined}
                        size={40}
                      />
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-foreground truncate">
                          {activePerson.person.name}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {activePerson.items.length} piece
                          {activePerson.items.length === 1 ? '' : 's'} of
                          content — tap to add to the line-up
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <button
                        onClick={() => selectAll(activePerson)}
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        Select all
                      </button>
                      <button
                        onClick={() =>
                          clearPerson(activePerson.person.userId)
                        }
                        className="text-xs font-medium text-muted-foreground hover:underline"
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => setActivePerson(null)}
                        className="p-1 rounded-full text-muted-foreground hover:text-destructive transition-colors"
                        aria-label="Close"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {activePerson.items.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      This person has no ready content.
                    </p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {activePerson.items.map((item: DemoBrowseItem) => {
                        const position = positionOf(item.id);
                        const isSelected = position !== undefined;
                        return (
                          <button
                            key={item.id}
                            onClick={() => toggleItem(activePerson, item.id)}
                            className={cn(
                              'relative rounded-lg overflow-hidden group border-2 transition-all',
                              isSelected
                                ? 'border-primary'
                                : 'border-transparent'
                            )}
                            style={{ aspectRatio: '3/4' }}
                          >
                            <img
                              src={item.posterUrl}
                              alt={item.caption || 'Content'}
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div
                              className={cn(
                                'absolute inset-0 transition-opacity',
                                isSelected
                                  ? 'bg-primary/25'
                                  : 'bg-black/0 group-hover:bg-black/20'
                              )}
                            />
                            {item.type === 'VIDEO' && (
                              <span className="absolute bottom-1.5 left-1.5 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center">
                                <Play className="h-3 w-3 text-white" />
                              </span>
                            )}
                            {/* Order badge: the number this item plays at */}
                            <span
                              className={cn(
                                'absolute top-1.5 right-1.5 min-w-[20px] h-5 px-1 rounded-full flex items-center justify-center border text-[11px] font-bold',
                                isSelected
                                  ? 'bg-primary border-primary text-white'
                                  : 'bg-black/40 border-white/60 text-transparent'
                              )}
                            >
                              {isSelected ? position : '0'}
                            </span>
                            {item.serviceTag && (
                              <span className="absolute bottom-1.5 right-1.5 max-w-[70%] truncate text-[9px] font-medium text-white bg-black/60 rounded px-1.5 py-0.5">
                                {item.serviceTag}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right: numbered line-up */}
            <div className="lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-6 lg:sticky lg:top-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-foreground">
                    Play order
                  </h3>
                  <span
                    className={cn(
                      'text-xs font-semibold rounded-full px-2.5 py-1',
                      items.length > 0
                        ? 'text-primary bg-primary/10'
                        : 'text-muted-foreground bg-muted/40'
                    )}
                  >
                    {items.length}/{DEMO_MAX_ITEMS}
                  </span>
                </div>

                {/* Ordering strategy */}
                <div className="flex items-center gap-1 rounded-lg bg-muted/30 p-1 mb-4">
                  <button
                    onClick={() => changeOrderBy('manual')}
                    className={cn(
                      'flex-1 text-xs font-semibold rounded-md px-2 py-1.5 transition-colors',
                      isManualOrder
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    Manual
                  </button>
                  <button
                    onClick={() => changeOrderBy('xuman_rating')}
                    className={cn(
                      'flex-1 text-xs font-semibold rounded-md px-2 py-1.5 transition-colors flex items-center justify-center gap-1',
                      !isManualOrder
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <Star className="h-3 w-3" />
                    Xuman rating
                  </button>
                </div>

                {sortedItems.length === 0 ? (
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Nothing selected yet. Search a person on the left and tap
                    their content — each tap appends it to the play order.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-[60vh] overflow-y-auto scrollbar-thin pr-1">
                    {sortedItems.map((item, idx) => {
                      const browse = browseItemFor(item);
                      return (
                        <div
                          key={item.contentId}
                          draggable={dragEnabledId === item.contentId}
                          onDragStart={(e) => {
                            dragIdRef.current = item.contentId;
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            if (dragOverId !== item.contentId) {
                              setDragOverId(item.contentId);
                            }
                          }}
                          onDragLeave={() =>
                            setDragOverId((prev) =>
                              prev === item.contentId ? null : prev
                            )
                          }
                          onDrop={(e) => {
                            e.preventDefault();
                            handleDrop(item.contentId);
                          }}
                          onDragEnd={() => {
                            dragIdRef.current = null;
                            setDragOverId(null);
                            setDragEnabledId(null);
                          }}
                          className={cn(
                            'flex items-center gap-2 rounded-lg px-2 py-2 border bg-background/60 transition-colors',
                            dragOverId === item.contentId
                              ? 'border-primary bg-primary/10'
                              : 'border-border'
                          )}
                        >
                          {isManualOrder ? (
                            <>
                              {/* Drag handle — the only place a drag can start */}
                              <span
                                onMouseDown={() => setDragEnabledId(item.contentId)}
                                onMouseUp={() => setDragEnabledId(null)}
                                className="cursor-grab active:cursor-grabbing text-muted-foreground flex-shrink-0"
                                aria-label="Drag to reorder"
                              >
                                <GripVertical className="h-4 w-4" />
                              </span>
                              {/* Editable position 1..N */}
                              <input
                                key={`${item.contentId}-${item.position}`}
                                type="number"
                                min={1}
                                max={sortedItems.length}
                                defaultValue={item.position}
                                onBlur={(e) => {
                                  const v = parseInt(e.target.value, 10);
                                  if (!Number.isNaN(v) && v !== item.position) {
                                    moveItem(item.contentId, v);
                                  } else {
                                    e.target.value = String(item.position);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    (e.target as HTMLInputElement).blur();
                                  }
                                }}
                                className="w-12 flex-shrink-0 rounded-md bg-background border border-border text-center text-sm font-bold text-primary py-1.5 focus:outline-none focus:border-primary/60 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                aria-label="Play position"
                              />
                            </>
                          ) : (
                            /* Computed position — comes from the rating sort */
                            <span className="w-12 flex-shrink-0 rounded-md bg-primary/10 text-center text-sm font-bold text-primary py-1.5">
                              {idx + 1}
                            </span>
                          )}
                          {/* Thumbnail */}
                          <div
                            className="w-9 h-12 rounded-md overflow-hidden bg-muted/40 flex-shrink-0"
                            style={{ aspectRatio: '3/4' }}
                          >
                            {browse?.posterUrl && (
                              <img
                                src={browse.posterUrl}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate flex items-center gap-1.5">
                              <span className="truncate">
                                {personNameFor(item.userId)}
                              </span>
                              {!isManualOrder && ratingFor(item.userId) >= 0 && (
                                <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground flex-shrink-0">
                                  <Star className="h-3 w-3 text-yellow-400" />
                                  {ratingFor(item.userId).toFixed(1)}
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {browse?.serviceTag ||
                                browse?.caption ||
                                item.contentId.slice(0, 8)}
                            </p>
                          </div>
                          {/* One-slot nudges (manual order only) */}
                          {isManualOrder && (
                            <div className="flex flex-col flex-shrink-0">
                              <button
                                onClick={() => moveItemBy(item.contentId, -1)}
                                disabled={item.position === 1}
                                className="p-0.5 rounded text-muted-foreground hover:text-primary disabled:opacity-25 disabled:hover:text-muted-foreground transition-colors"
                                aria-label="Move up"
                              >
                                <ChevronUp className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => moveItemBy(item.contentId, 1)}
                                disabled={item.position === sortedItems.length}
                                className="p-0.5 rounded text-muted-foreground hover:text-primary disabled:opacity-25 disabled:hover:text-muted-foreground transition-colors"
                                aria-label="Move down"
                              >
                                <ChevronDown className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                          <button
                            onClick={() => removeItem(item.contentId)}
                            className="p-1 rounded-full flex-shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                            aria-label="Remove from line-up"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <p className="text-xs mt-4 leading-relaxed text-muted-foreground">
                  {isManualOrder ? (
                    <>
                      Number 1 plays first. Reorder however you like — drag
                      rows, use the arrows, or type a number; the rest shift
                      automatically. Nothing changes on the website until you
                      press Update.
                    </>
                  ) : (
                    <>
                      Order is computed from each owner&apos;s current Xuman
                      rating (highest = 1) every time the website loads — the
                      numbers here are a preview and update automatically as
                      ratings change. Your manual order is kept and comes
                      back when you switch to Manual. Press Update to
                      publish.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {lastUpdatedAt && !dirty && (
          <p className="text-xs text-muted-foreground">
            Last published: {new Date(lastUpdatedAt).toLocaleString()}
          </p>
        )}
      </div>

      {/* Sticky update bar */}
      {dirty && (
        <div className="fixed bottom-0 left-0 lg:left-64 right-0 z-30 border-t border-border bg-card/95 backdrop-blur px-6 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              Unsaved changes — nothing goes live until you press Update.
            </p>
            <button
              onClick={handleUpdate}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saving ? 'Updating…' : 'Update'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Avatar: React.FC<{ name: string; url?: string; size: number }> = ({
  name,
  url,
  size,
}) => (
  <div
    className="rounded-full overflow-hidden flex items-center justify-center bg-primary/15 flex-shrink-0"
    style={{ width: size, height: size }}
  >
    {url ? (
      <img src={url} alt={name} className="w-full h-full object-cover" />
    ) : (
      <span
        className="font-semibold text-primary"
        style={{ fontSize: size * 0.4 }}
      >
        {(name || '?').charAt(0).toUpperCase()}
      </span>
    )}
  </div>
);

export default DemoContent;
